import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { SkeletonPosMakeSales } from '@/components/ui/Skeleton'
import { IconCompress, IconExpand } from '@/components/icons'
import { useUI } from '@/context/UIContext'
import { formatCatalogUpdatedLabel } from './offline/catalogAge'
import type { CachedPosProduct } from './offline/types'
import { PosProductCard } from './PosProductCard'
import { cn } from '@/lib/utils'

export type PosCategoryChip = {
  slug: string
  name: string
}

type Props = {
  search: string
  onSearchChange: (value: string) => void
  categorySlug: string | null
  onCategoryChange: (slug: string | null) => void
  categories: PosCategoryChip[]
  products: CachedPosProduct[]
  cartQuantities: Record<number, number>
  currency: string
  lowStockThreshold: number
  addedHighlightProductId: number | null
  productsLoading: boolean
  error: string | null
  catalogUpdatedAt: number | null
  catalogStale: boolean
  isOnline: boolean
  queueCount: number
  onRefreshCatalog: () => void
  onOpenQueue: () => void
  onAddProduct: (product: CachedPosProduct) => void
}

export function PosCatalogPanel({
  search,
  onSearchChange,
  categorySlug,
  onCategoryChange,
  categories,
  products,
  cartQuantities,
  currency,
  lowStockThreshold,
  addedHighlightProductId,
  productsLoading,
  error,
  catalogUpdatedAt,
  catalogStale,
  isOnline,
  queueCount,
  onRefreshCatalog,
  onOpenQueue,
  onAddProduct,
}: Props) {
  const { posFocusMode, togglePosFocusMode } = useUI()

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col">
      <div className="mb-3 flex shrink-0 flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-foreground text-lg font-semibold tracking-tight">Point of Sale</h2>
          <p className="text-muted-foreground mt-0.5 text-sm">Tap a product to add it to the sale.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {queueCount > 0 ? (
            <Button type="button" variant="secondary" className="min-h-10 shrink-0" onClick={onOpenQueue}>
              Sync queue
              <span className="bg-primary/15 ml-2 rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums">
                {queueCount}
              </span>
            </Button>
          ) : null}
          <Button
            type="button"
            variant={posFocusMode ? 'secondary' : 'outline'}
            size="sm"
            className="min-h-10 shrink-0 gap-1.5"
            onClick={togglePosFocusMode}
            aria-pressed={posFocusMode}
            title={
              posFocusMode
                ? 'Exit focus mode (Esc) — show the side menu'
                : 'Focus mode — hide the side menu for more product space'
            }
          >
            {posFocusMode ? (
              <IconCompress className="h-4 w-4 shrink-0" aria-hidden />
            ) : (
              <IconExpand className="h-4 w-4 shrink-0" aria-hidden />
            )}
            {posFocusMode ? 'Exit focus' : 'Focus'}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="min-h-10 shrink-0"
            disabled={!isOnline || productsLoading}
            onClick={onRefreshCatalog}
          >
            Refresh
          </Button>
        </div>
      </div>

      <div className="text-muted-foreground mb-3 flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1 text-xs">
        <span
          className={cn(
            'inline-flex items-center rounded-full px-2 py-0.5 font-medium',
            isOnline ? 'bg-muted/60 text-foreground' : 'bg-amber-500/15 text-amber-900 dark:text-amber-100',
          )}
        >
          {isOnline ? 'Online' : 'Offline'}
        </span>
        {catalogUpdatedAt != null ? (
          <span>
            Catalog:{' '}
            <span className="text-foreground font-medium">{formatCatalogUpdatedLabel(catalogUpdatedAt)}</span>
          </span>
        ) : (
          <span>Catalog: not loaded yet</span>
        )}
        {catalogStale ? (
          <span className="text-amber-800 dark:text-amber-200">Prices/stock may be outdated</span>
        ) : null}
      </div>

      <label className="mb-3 block shrink-0 space-y-1.5">
        <span className="sr-only">Search products</span>
        <Input
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search by name or SKU"
          className="h-12 text-base"
        />
      </label>

      {categories.length > 0 ? (
        <div
          className="mb-3 flex shrink-0 gap-2 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          role="tablist"
          aria-label="Product categories"
        >
          <button
            type="button"
            role="tab"
            aria-selected={categorySlug == null}
            onClick={() => onCategoryChange(null)}
            className={cn(
              'min-h-10 shrink-0 rounded-full border px-3.5 text-sm font-medium transition',
              'focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
              categorySlug == null
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-border bg-background text-foreground hover:bg-muted/60',
            )}
          >
            All products
          </button>
          {categories.map((c) => (
            <button
              key={c.slug}
              type="button"
              role="tab"
              aria-selected={categorySlug === c.slug}
              onClick={() => onCategoryChange(c.slug)}
              className={cn(
                'min-h-10 shrink-0 rounded-full border px-3.5 text-sm font-medium transition',
                'focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
                categorySlug === c.slug
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border bg-background text-foreground hover:bg-muted/60',
              )}
            >
              {c.name}
            </button>
          ))}
        </div>
      ) : null}

      {error ? <p className="text-destructive mb-3 shrink-0 text-sm">{error}</p> : null}

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pb-24 lg:pb-2">
        {productsLoading ? (
          <SkeletonPosMakeSales variant="products" />
        ) : products.length === 0 ? (
          <p className="text-muted-foreground px-2 py-10 text-center text-sm">No matching products</p>
        ) : (
          <ul
            className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4"
            aria-label="Products"
          >
            {products.map((p) => (
              <li key={p.id}>
                <PosProductCard
                  product={p}
                  currency={currency}
                  inCartQty={cartQuantities[p.id] ?? 0}
                  lowStockThreshold={lowStockThreshold}
                  justAdded={addedHighlightProductId === p.id}
                  onAdd={() => onAddProduct(p)}
                />
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
